<template>
  <div class="mb-4">
    <div class="relative">
     

      <div class="relative">
        <!-- Hidden file input -->
        <input
          ref="fileInput"
          type="file"
          :accept="accept"
          :multiple="multiple"
          :disabled="disabled"
          @change="handleFileChange"
          class="hidden"
          v-bind="$attrs"
        />
        
        <!-- Custom file input display -->
        <div
          @click="triggerFileInput"
          :class="fileInputClasses"
          :tabindex="disabled ? -1 : 0"
          @keydown.enter="triggerFileInput"
          @keydown.space.prevent="triggerFileInput"
        >
          <div class="flex items-center justify-center">
            <i v-if="prefixIcon" :class="['mr-2', prefixIcon]"></i>
            <i v-else class="fa fa-cloud-upload-alt mr-2 text-gray-400"></i>
            <div class="w-2"></div>
            <span class="file-text">{{ displayText }}</span>
          
          </div>
        </div>
        
        <!-- File preview area -->
        <div v-if="showPreview && selectedFiles.length > 0" class="mt-3">
          <div class="file-preview-container">
            <div
              v-for="(file, index) in selectedFiles"
              :key="index"
              class="file-preview-item"
            >
              <div class="file-info">
                <i :class="getFileIcon(file.type)"></i>
                <div class="file-details">
                  <span class="file-name">{{ file.name }}</span>
                  <span class="file-size">{{ formatFileSize(file.size) }}</span>
                </div>
              </div>
              <button
                v-if="!disabled"
                @click="removeFile(index)"
                class="remove-file-btn"
                type="button"
              >
                <i class="fa fa-times"></i>
              </button>
            </div>
          </div>
        </div>
      </div>

      <!-- Error message -->
      <div v-if="isError && errorMsg" class="error-message mt-2 flex items-center text-red-500 text-sm">
        <i class="fa fa-exclamation-circle mr-2"></i>
        {{ errorMsg }}
      </div>
      
      <!-- Helper text -->
      <div v-if="helperText && !isError" class="helper-text mt-2 text-gray-500 dark:text-gray-400 text-sm">
        {{ helperText }}
      </div>
    </div>
  </div>
</template>

<script setup>
import { defineProps, defineEmits, ref, computed } from "vue";

const props = defineProps({
  label: {
    type: String,
    default: "",
  },
  placeholder: {
    type: String,
    default: "Choose file or drag and drop",
  },
  modelValue: {
    type: [File, FileList, Array],
    default: null,
  },
  isError: {
    type: Boolean,
    default: false,
  },
  errorMsg: {
    type: String,
    default: "",
  },
  disabled: {
    type: Boolean,
    default: false,
  },
  required: {
    type: Boolean,
    default: false,
  },
  helperText: {
    type: String,
    default: "",
  },
  accept: {
    type: String,
    default: "*",
  },
  multiple: {
    type: Boolean,
    default: false,
  },
  maxSize: {
    type: Number,
    default: null, // in MB
  },
  showPreview: {
    type: Boolean,
    default: true,
  },
  size: {
    type: String,
    default: "md",
    validator: (value) => ["sm", "md", "lg"].includes(value),
  },
  variant: {
    type: String,
    default: "default",
    validator: (value) => ["default", "filled", "outlined"].includes(value),
  },
  prefixIcon: {
    type: String,
    default: "",
  },
});

const emit = defineEmits(["update:modelValue", "focus", "blur", "change"]);

const fileInput = ref(null);
const selectedFiles = ref([]);

// Size classes
const sizeClasses = computed(() => {
  const sizes = {
    sm: "px-3 py-2 text-sm min-h-[36px]",
    md: "px-4 py-2.5 text-sm min-h-[42px]",
    lg: "px-4 py-3 text-base min-h-[48px]",
  };
  return sizes[props.size];
});

// Variant classes
const variantClasses = computed(() => {
  const variants = {
    default: "bg-white dark:bg-bgFullDark border border-gray-300 dark:border-gray-600",
    filled: "bg-gray-50 dark:bg-gray-700 border-0",
    outlined: "bg-transparent border-2 border-gray-300 dark:border-gray-600",
  };
  return variants[props.variant];
});

// File input classes
const fileInputClasses = computed(() => {
  const baseClasses = [
    "block w-full rounded-lg transition-all duration-200 ease-in-out",
    "focus:outline-none focus:ring-2 focus:ring-opacity-50",
    "disabled:opacity-50 disabled:cursor-not-allowed",
    "cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-800",
    "border-dashed border-2",
    sizeClasses.value,
    variantClasses.value,
  ];

  // Error state
  if (props.isError) {
    baseClasses.push(
      "border-red-500 text-red-500 focus:ring-red-500",
      "dark:border-red-600 dark:text-red-500"
    );
  } else {
    // Normal state
    baseClasses.push(
      "text-textBlack1 dark:text-white",
      "border-gray-300 dark:border-gray-600",
      "focus:ring-blue-500 focus:border-blue-500"
    );
  }

  // Disabled state
  if (props.disabled) {
    baseClasses.push("bg-gray-100 dark:bg-gray-800 cursor-not-allowed");
  }

  return baseClasses.join(" ");
});

// Display text
const displayText = computed(() => {
  if (selectedFiles.value.length === 0) {
    return props.placeholder;
  }
  
  if (props.multiple) {
    return `${selectedFiles.value.length} file(s) selected`;
  }
  
  return selectedFiles.value[0]?.name || props.placeholder;
});

// Event handlers
const triggerFileInput = () => {
  if (!props.disabled && fileInput.value) {
    fileInput.value.click();
  }
};

const handleFileChange = (event) => {
  const files = Array.from(event.target.files);
  
  // Validate file size
  if (props.maxSize) {
    const maxSizeBytes = props.maxSize * 1024 * 1024; // Convert MB to bytes
    const oversizedFiles = files.filter(file => file.size > maxSizeBytes);
    
    if (oversizedFiles.length > 0) {
      emit("update:modelValue", null);
      // You could emit an error event here
      return;
    }
  }
  
  selectedFiles.value = files;
  
  if (props.multiple) {
    emit("update:modelValue", files);
  } else {
    emit("update:modelValue", files[0] || null);
  }
  
  emit("change", files);
};

const removeFile = (index) => {
  selectedFiles.value.splice(index, 1);
  
  if (props.multiple) {
    emit("update:modelValue", selectedFiles.value);
  } else {
    emit("update:modelValue", selectedFiles.value[0] || null);
  }
};

const getFileIcon = (fileType) => {
  if (fileType.startsWith('image/')) return 'fa fa-image text-blue-500';
  if (fileType.startsWith('video/')) return 'fa fa-video text-purple-500';
  if (fileType.startsWith('audio/')) return 'fa fa-music text-green-500';
  if (fileType.includes('pdf')) return 'fa fa-file-pdf text-red-500';
  if (fileType.includes('word')) return 'fa fa-file-word text-blue-600';
  if (fileType.includes('excel') || fileType.includes('spreadsheet')) return 'fa fa-file-excel text-green-600';
  if (fileType.includes('powerpoint') || fileType.includes('presentation')) return 'fa fa-file-powerpoint text-orange-500';
  if (fileType.includes('zip') || fileType.includes('rar')) return 'fa fa-file-archive text-yellow-500';
  return 'fa fa-file text-gray-500';
};

const formatFileSize = (bytes) => {
  if (bytes === 0) return '0 Bytes';
  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
};
</script>

<style scoped>
/* File input hover effects */
.file-input-display:hover:not(:disabled) {
  transform: translateY(-1px);
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
}

/* File preview container */
.file-preview-container {
  @apply space-y-2;
}

.file-preview-item {
  @apply flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700;
}

.file-info {
  @apply flex items-center space-x-3;
}

.file-details {
  @apply flex flex-col;
}

.file-name {
  @apply text-sm font-medium text-gray-900 dark:text-white;
}

.file-size {
  @apply text-xs text-gray-500 dark:text-gray-400;
}

.remove-file-btn {
  @apply p-1 text-gray-400 hover:text-red-500 transition-colors duration-200;
}

/* Error message animation */
.error-message {
  animation: slideDown 0.3s ease-out;
}

@keyframes slideDown {
  from {
    opacity: 0;
    transform: translateY(-4px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

/* Helper text styling */
.helper-text {
  transition: all 0.2s ease-in-out;
}

/* Disabled state styling */
.file-input-display:disabled {
  background-color: rgba(243, 244, 246, 0.5);
  color: rgba(107, 114, 128, 0.7);
  cursor: not-allowed;
}

.dark .file-input-display:disabled {
  background-color: rgba(31, 41, 55, 0.5);
  color: rgba(156, 163, 175, 0.7);
}

/* Drag and drop styling */
.file-input-display.drag-over {
  @apply border-blue-500 bg-blue-50 dark:bg-blue-900/20;
}

/* Focus states */
.file-input-display:focus {
  outline: none;
  box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
}

/* Dark mode adjustments */
.dark .file-preview-item {
  @apply bg-gray-800 border-gray-700;
}

.dark .file-input-display:hover:not(:disabled) {
  @apply bg-gray-800;
}
</style>
